/* Created by Interactive 63 - https://interactive63.com */
/**
 * Timeline ruler arithmetic: converting between time and pixels, choosing readable tick
 * spacing for the current zoom, and labelling ticks.
 */

export interface TimelineView {
  /** Pixels per second. */
  zoom: number;
  /** Horizontal scroll in pixels. */
  scroll: number;
}

/** Space left of time 0 so the first keyframe is not clipped by the panel edge. */
export const TIMELINE_PAD = 12;
export const MIN_TIMELINE_ZOOM = 20;
export const MAX_TIMELINE_ZOOM = 2000;
const TICK_STEPS_MS = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];
const MIN_LABEL_SPACING_PX = 60;

export interface Tick {
  time: number;
  x: number;
  label: string;
}

/** Converts a time in milliseconds to an x position in the keyframe area. */
export function timeToX(time: number, view: TimelineView): number {
  return (time / 1000) * view.zoom - view.scroll + TIMELINE_PAD;
}

/** Converts an x position in the keyframe area to a time in milliseconds. */
export function xToTime(x: number, view: TimelineView): number {
  return ((x - TIMELINE_PAD + view.scroll) / view.zoom) * 1000;
}

/** Limits a zoom level to the supported range. */
export function clampTimelineZoom(zoom: number): number {
  return Math.min(MAX_TIMELINE_ZOOM, Math.max(MIN_TIMELINE_ZOOM, zoom));
}

/** The smallest tick step that leaves at least 60 px between labelled ticks. */
export function chooseTickStep(zoom: number): number {
  const step = TICK_STEPS_MS.find((ms) => (ms / 1000) * zoom >= MIN_LABEL_SPACING_PX);
  return step ?? TICK_STEPS_MS[TICK_STEPS_MS.length - 1] ?? 10000;
}

/** Formats a tick time as seconds: 0s, 0.5s, 1s, 1.25s. */
export function formatTickLabel(ms: number): string {
  const seconds = Number((ms / 1000).toFixed(3));
  return `${seconds}s`;
}

/** Computes the labelled and minor ticks visible in a viewport of the given width. */
export function computeTicks(
  view: TimelineView,
  width: number,
): { major: Tick[]; minor: number[]; step: number } {
  const step = chooseTickStep(view.zoom);
  const minorStep = step / 5;
  const start = Math.max(0, Math.floor(xToTime(0, view) / minorStep) * minorStep);
  const end = xToTime(width, view);
  const major: Tick[] = [];
  const minor: number[] = [];
  for (let time = start; time <= end + minorStep; time += minorStep) {
    const rounded = Math.round(time * 1000) / 1000;
    const x = timeToX(rounded, view);
    if (Math.abs(rounded % step) < 1e-6 || Math.abs((rounded % step) - step) < 1e-6) {
      major.push({ time: rounded, x, label: formatTickLabel(rounded) });
    } else {
      minor.push(x);
    }
  }
  return { major, minor, step };
}

/** Finds the zoom that fits a duration into a width, leaving a little room at the end. */
export function fitZoom(duration: number, width: number): number {
  const usable = Math.max(1, width - 40 - TIMELINE_PAD);
  return clampTimelineZoom((usable / duration) * 1000);
}
